import { axios } from "@/lib/axios";

const prefix = "/api/front/user/leaderboard";
type AxiosResponse<T> = [
    boolean,
    T,
];

export const enum RankingEnum {
    Profit = "profit",
    Volume = "volume",
    predictions = "predictions",
}

export const enum TimePeriodEnum {
    Day = "24h",
    Week = "7d",
    Month = "30d",
    ThreeMonth = "3month",
}

// 获取排行榜
type LeaderboardParams = {
    /**
     * 页码
     */
    pageNum: number;
    /**
     * 每页大小（最大50）
     */
    pageSize: number;
    /**
     * 排行榜类型：profit(收益), volume(成交量), predictions(预测量)
     */
    rankingType: RankingEnum;
    /**
     * 时间维度：24h, 7d, 30d, 3month
     */
    timePeriod: TimePeriodEnum;
};
type LeaderboardResponse = {
    /**
     * 当前用户排名信息
     */
    currentUserRank?: UserRankResponse;
    /**
     * 排行榜列表
     */
    leaderboard?: LeaderboardItemResponse[];
    /**
     * 排行榜类型
     */
    rankingType?: string;
    /**
     * 时间维度
     */
    timePeriod?: string;
    /**
     * 总记录数
     */
    total?: number;
};

export type LeaderboardItemResponse = {
    /**
     * 预测数量（predictions类型排行榜使用）
     */
    predictionCount?: number;
    /**
     * 收益金额（profit类型排行榜使用）
     */
    profitAmount?: number;
    /**
     * 排名
     */
    rank?: number;
    /**
     * 用户头像
     */
    userAvatar?: string;
    /**
     * 用户ID
     */
    userId?: number;
    /**
     * 用户昵称
     */
    userName?: string;
    /**
     * 成交量（volume类型排行榜使用）
     */
    volumeAmount?: number;
    userDesc?: string; // 用户说明
    isFollowing: boolean; // 是否关注
};

export function apiUserLeaderboardList( data: LeaderboardParams ): Promise<
    AxiosResponse<{
        data: LeaderboardResponse;
    }>
> {
    return axios.post( `${prefix}/list`, data );
}

export type UserRankResponse = {
    /**
     * 预测数量
     */
    predictionCount?: number;
    /**
     * 收益金额
     */
    profitAmount?: number;
    /**
     * 排名位置（null表示未进入前50）
     */
    rank?: number;
    /**
     * 排行榜类型：profit(收益), volume(成交量), predictions(预测量)
     */
    rankingType?: RankingEnum;
    /**
     * 时间维度：24h, 7d, 30d, 3month
     */
    timePeriod?: TimePeriodEnum;
    /**
     * 用户ID
     */
    userId?: number;
    /**
     * 成交量
     */
    volumeAmount?: number;
    /**
     * 是否在排行
     */
    isOnLeaderboard: boolean;
};

// 获取当前用户的排名
export function apiUserLeaderboardMyRank( { timePeriod, rankingType }: {
    timePeriod: TimePeriodEnum;
    rankingType: RankingEnum;
} ): Promise<
    AxiosResponse<{
        data: UserRankResponse;
    }>
> {
    return axios.get( `${prefix}/my-rank`, {
        params: {
            timePeriod,
            rankingType,
        },
    } );
}

export type UserEventInfo = {
    userId: string;
    eventCount: number;
    recentEventCovers: string[];
};
type UserEventsResponse = {
    timePeriod: TimePeriodEnum;
    userEventsMap: Record<string, UserEventInfo>;
};

// 批量获取用户参与的事件信息
export function apiUserLeaderboardUserEvents( data: {
    userIds: number[];
    timePeriod: TimePeriodEnum;
} ): Promise<
    AxiosResponse<{
        data: UserEventsResponse;
    }>
> {
    return axios.post( `${prefix}/user-events`, data );
}

// 获取关注用户排行榜
// /api/user/leaderboard/following
export function apiUserLeaderboardFollowing( data: {
    pageNum: number;
    pageSize: number;
    rankingType: RankingEnum;
    timePeriod: TimePeriodEnum;
} ): Promise<
    AxiosResponse<{
        data: LeaderboardResponse;
    }>
> {
    return axios.post( `${prefix}/following`, {
        data,
    } );
}

type UserRecommendResponse = {
    /**
     * 是否还有更多数据
     */
    hasMore?: boolean;
    /**
     * 提示信息（当无数据或到达底部时）
     */
    message?: string;
    /**
     * 推荐用户列表
     */
    recommendUsers?: RecommendUser[];
};

export type RecommendUser = {
    /**
     * 连续活跃天数
     */
    activeDays?: number;
    /**
     * 被关注数
     */
    followerCount?: number;
    /**
     * 是否已关注
     */
    isFollowing?: boolean;
    /**
     * 最近7天盈利金额（仅热门排行有值）
     */
    profitAmount?: number;
    /**
     * 盈亏率（最近7天）
     * 计算公式：(盈利或亏损 / 投入金额) * 100%
     */
    profitRate?: number;
    /**
     * 排行榜排名（仅热门排行有值）
     */
    rank?: number;
    /**
     * 推荐原因（content: 内容偏好, hot: 热门排行）
     */
    recommendReason?: "content" | "hot";
    /**
     * 用户头像
     */
    userAvatar?: string;
    /**
     * 用户ID
     */
    userId?: number;
    /**
     * 用户昵称
     */
    userName?: string;
};

// 获取推荐关注用户列表
export function apiUserLeaderboardRecommend( data: {
    pageNum: number;
    pageSize: number;
} ): Promise<
    AxiosResponse<{
        data: UserRecommendResponse;
    }>
> {
    return axios.post( `${prefix}/recommend`, data );
}

export type ActivityItem = {
    /**
     * 活动类型 EnumActivityType
     */
    activityType?: number;
    /**
     * 金额（下单金额或盈利金额）
     */
    amount?: number;
    /**
     * 事件名称
     */
    eventName?: string;
    /**
     * 事件id
     */
    eventNo?: number;
    /**
     * 活动时间戳
     */
    timestamp?: number;
    /**
     * 用户头像
     */
    userAvatar?: string;
    /**
     * 用户ID
     */
    userId?: number;
    /**
     * 用户昵称
     */
    userName?: string;
    eventCategory: number;
};

// 动态获取用户下单活动状态
export function apiActivityFeed(): Promise<
    AxiosResponse<{
        data: {
            activities: ActivityItem[];
        };
    }>
> {
    return axios.get( `${prefix}/activity-feed` );
}

// 关注用户
export function apiUserSocialFollow( data: {
    followingUserId: string;
} ): Promise<AxiosResponse<any>> {
    return axios.post( "/api/front/user/social/follow", data );
}

// 取消关注用户
export function apiUserSocialUnfollow( data: {
    followingUserId: string;
} ): Promise<AxiosResponse<any>> {
    return axios.post( "/api/front/user/social/unfollow", data );
}

// 统一的关注/取消关注方法
export function apiUserSocialToggleFollow(
    followingUserId: string,
    isFollow: boolean,
): Promise<AxiosResponse<any>> {
    if ( isFollow ) {
        return apiUserSocialFollow( { followingUserId } );
    } else {
        return apiUserSocialUnfollow( { followingUserId } );
    }
}

// 查询关注用户的排行榜
export interface FollowingLeaderboardResponse {
    /**
     * 当前用户的营收信息（新增）
     */
    currentUserRank?: UserRankResponse;
    /**
     * 关注用户列表
     */
    followingUsers?: FollowingUserRank[];
    /**
     * 时间维度
     */
    timePeriod?: string;
    /**
     * 关注的用户总数
     */
    totalFollowingCount?: number;

    [property: string]: any;
}

/**
 * 关注用户排名信息
 *
 * FollowingUserRank
 */
export interface FollowingUserRank {
    /**
     * 关注时间
     */
    followTime?: string;
    /**
     * 盈亏金额
     */
    profitAmount?: number;
    /**
     * 排名（如果未进入前50，则为null）
     */
    rank?: number;
    /**
     * 用户头像
     */
    userAvatar?: string;
    /**
     * 用户ID
     */
    userId?: number;
    /**
     * 用户昵称
     */
    userName?: string;

    [property: string]: any;
}

export function apiUserLeaderboardFollowingList( data: {
    timePeriod: TimePeriodEnum;
    pageNum: number;
    pageSize: number;
} ): Promise<
    AxiosResponse<{
        data: FollowingLeaderboardResponse;
    }>
> {
    return axios.post( `${prefix}/following`, data );
}

// User Social Feature APIs
const socialPrefix = "/api/front/user/social";

export type UserSocialStatsResponse = {
    userId?: number;
    followerCount?: number;
    followingCount?: number;
    profileViewCount?: number;
    uniqueVisitorCount?: number;
};

// Get user social statistics
export function apiUserSocialStats( userId: string | number ): Promise<
    AxiosResponse<{
        data: UserSocialStatsResponse;
    }>
> {
    return axios.get( `${socialPrefix}/stats/${userId}` );
}

// Check if following
export function apiUserSocialIsFollowing( userId: string | number ): Promise<
    AxiosResponse<{
        data: boolean;
    }>
> {
    return axios.get( `${socialPrefix}/is-following/${userId}` );
}

/**
 * 当前查看者与该用户的关系
 */
export enum ViewerRelation {
    FollowedBy = "FOLLOWED_BY",
    Following = "FOLLOWING",
    Mutual = "MUTUAL",
    None = "NONE",
    Self = "SELF",
}

type UserListQueryReq = {
    pageNum?: number;
    pageSize?: number;
    userId?: number;
};

/**
 * 用户简要信息响应
 *
 * UserBriefInfoResponse
 */
export type UserBriefInfoResponse = {
    /**
     * 用户头像
     */
    avatar?: string;
    /**
     * 用户简介
     */
    bio?: string;
    /**
     * 关注时间（仅在关注/粉丝列表中使用）
     */
    followTime?: string;
    /**
     * 最后浏览时间（仅在访客列表中使用）
     */
    lastViewTime?: string;
    /**
     * 是否互相关注
     */
    mutualFollow?: boolean;
    /**
     * 用户昵称
     */
    nickname?: string;
    /**
     * 用户ID
     */
    userId?: number;
    /**
     * 浏览次数（仅在访客列表中使用）
     */
    viewCount?: number;
    /**
     * 当前查看者与该用户的关系
     */
    viewerRelation?: ViewerRelation;
    [property: string]: any;
};

type PageUserBriefInfoResponse = {
    records?: UserBriefInfoResponse[];
    total?: number;
    size?: number;
    current?: number;
    orders?: Array<{
        column?: string;
        asc?: boolean;
    }>;
    optimizeCountSql?: boolean;
    searchCount?: boolean;
    optimizeJoinOfCountSql?: boolean;
    maxLimit?: number;
    countId?: string;
};

// Get following list
export function apiUserSocialFollowingList( data: UserListQueryReq ): Promise<
    AxiosResponse<{
        data: PageUserBriefInfoResponse;
    }>
> {
    return axios.post( `${socialPrefix}/following/list`, data );
}

// Get follower list
export function apiUserSocialFollowerList( data: UserListQueryReq ): Promise<
    AxiosResponse<{
        data: PageUserBriefInfoResponse;
    }>
> {
    return axios.post( `${socialPrefix}/follower/list`, data );
}

// Record profile view (only for recording visit, returns void)
export function apiUserSocialProfileView(
    profileUserId: string | number,
): Promise<
    AxiosResponse<{
        data: null;
    }>
> {
    return axios.post( `${socialPrefix}/profile-view/${profileUserId}` );
}

// Get profile visitor list
export function apiUserSocialVisitorList( data: UserListQueryReq ): Promise<
    AxiosResponse<{
        data: PageUserBriefInfoResponse;
    }>
> {
    return axios.post( `${socialPrefix}/visitor/list`, data );
}
